import { learningApi } from './client';
import type {
  ArcadeAbandonResult,
  ArcadeAnswerResult,
  ArcadeCompleteResult,
  ArcadePersonalBest,
  ArcadePersonalBestsResponse,
  ArcadeRun,
  ArcadeRunState,
  ArcadeTiming,
  DrillAttemptResult,
  DrillAudio,
  DrillItem,
  DrillSession,
  Flashcard,
  GhostRun,
  GhostTraceResponse,
  ListenPickAudio,
  ListenPickQuestion,
  SentenceScrambleQuestion,
  SpeedMatchQuestion,
  SpeedMatchRun,
  SpeedMatchRunState,
} from '@zolotalk/api-client';

export { learningApi };
export type {
  ArcadeAnswerResult,
  ArcadeCompleteResult,
  ArcadePersonalBest,
  ArcadePersonalBestsResponse,
  ArcadeRun,
  ArcadeRunState,
  DrillAttemptResult,
  DrillAudio,
  DrillItem,
  DrillSession,
  Flashcard,
  GhostRun,
  GhostTraceResponse,
  ListenPickAudio,
  ListenPickQuestion,
  SpeedMatchQuestion,
  SpeedMatchRun,
  SpeedMatchRunState,
};

export async function getDueFlashcards(): Promise<Flashcard[]> {
  return learningApi.getDueFlashcards();
}

export function reviewFlashcard(id: string, quality: 1 | 5): Promise<Flashcard> {
  return learningApi.reviewFlashcard(id, quality);
}

export function createDrillSession(): Promise<DrillSession> {
  return learningApi.createDrillSession();
}

export function getDrillAudio(itemId: string): Promise<DrillAudio> {
  return learningApi.getDrillAudio(itemId);
}

/** REQ-MOB-019: server-authoritative phrase assessment (no client scoring). */
export function submitDrillAttempt(itemId: string, audio: Blob): Promise<DrillAttemptResult> {
  return learningApi.submitDrillAttempt(itemId, audio);
}

export interface SpeedMatchGrade {
  questionId: string;
  correct: boolean;
  correctAnswer: string;
  scoreDelta: number;
  score: number;
  combo: number;
  bestCombo: number;
  answeredCount: number;
  correctCount: number;
  accuracy: number;
  complete: boolean;
  status: SpeedMatchRun['status'];
  gradeStatus: 'accepted' | 'duplicate';
  deadlineAt: string | null;
  verifiedScore: number | null;
  personalBest: number | null;
  isNewPersonalBest: boolean;
}

export interface SpeedMatchComplete {
  runId: string;
  status: SpeedMatchRun['status'];
  verifiedScore: number;
  bestCombo: number;
  correctCount: number;
  answeredCount: number;
  accuracy: number;
  personalBest: number;
  isNewPersonalBest: boolean;
}

/**
 * Narrow ArcadeRun → SpeedMatchRun via mode + question-type check (no cast).
 * Fails closed if the API returns a different mode or mixed question types.
 */
function isSpeedMatchRun(run: ArcadeRun): run is SpeedMatchRun {
  return (
    run.mode === 'speed_match' &&
    run.questions.every((q): q is SpeedMatchQuestion => q.type === 'speed-match')
  );
}

function isSpeedMatchRunState(run: ArcadeRunState): run is SpeedMatchRunState {
  return (
    run.mode === 'speed_match' &&
    run.questions.every((q): q is SpeedMatchQuestion => q.type === 'speed-match')
  );
}

/**
 * Speed Match Grid (FR-4.7 / COR-187): uses the shared typed arcade client.
 *
 * Two intentional spellings (do not collapse — they name different concepts):
 * - ArcadeMode storage/wire key: `speed_match` (snake_case) — personal-best row key
 * - GameType question content id: `speed-match` (hyphenated) — same family as word-match
 *
 * COR-184 / mobile maps UI `speed-match` → API `speed_match` the same way.
 */
export async function createSpeedMatchRun(timing: 'standard' | 'relaxed'): Promise<SpeedMatchRun> {
  const run = await learningApi.createArcadeRun({
    mode: 'speed_match',
    timing,
    types: ['speed-match'],
    limit: 6,
  });
  if (!isSpeedMatchRun(run)) {
    throw new Error(`expected speed_match arcade run, got mode=${run.mode}`);
  }
  return run;
}

export function answerSpeedMatchPair(
  runId: string,
  questionId: string,
  answer: string
): Promise<SpeedMatchGrade> {
  return learningApi.answerArcadeQuestion(runId, {
    questionId,
    answer,
  });
}

export async function getSpeedMatchRun(runId: string): Promise<SpeedMatchRunState> {
  const run = await learningApi.getArcadeRun(runId);
  if (!isSpeedMatchRunState(run)) {
    throw new Error(`expected speed_match arcade run state, got mode=${run.mode}`);
  }
  return run;
}

export function completeSpeedMatchRun(runId: string): Promise<SpeedMatchComplete> {
  return learningApi.completeArcadeRun(runId);
}

export function abandonSpeedMatchRun(runId: string): Promise<void> {
  return learningApi.abandonArcadeRun(runId).then(() => undefined);
}

/**
 * Listen & Pick (FR-4.9 / COR-189): first-class `listen_pick` mode so personal
 * bests never share a row with Rapid Fire.
 *
 * Two intentional spellings:
 * - ArcadeMode storage/wire key: `listen_pick` (snake_case) — personal-best key
 * - GameType question content id: `listen-pick` (hyphenated)
 */
export function createListenPickRun(): Promise<ArcadeRun> {
  return learningApi.createArcadeRun({
    mode: 'listen_pick',
    timing: 'relaxed',
    types: ['listen-pick'],
    limit: 5,
  });
}

export function getListenPickAudio(audioRef: string): Promise<ListenPickAudio> {
  return learningApi.getListenPickAudio(audioRef);
}

export function answerListenPick(
  runId: string,
  questionId: string,
  answer: string
): Promise<ArcadeAnswerResult> {
  return learningApi.answerArcadeQuestion(runId, { questionId, answer });
}

export function resumeListenPickRun(runId: string): Promise<ArcadeRunState> {
  return learningApi.getArcadeRun(runId);
}

export function completeListenPickRun(runId: string): Promise<ArcadeCompleteResult> {
  return learningApi.completeArcadeRun(runId);
}

export function abandonListenPickRun(runId: string) {
  return learningApi.abandonArcadeRun(runId);
}

export type {
  ArcadeAnswerResult as SentenceScrambleGrade,
  ArcadeCompleteResult as SentenceScrambleComplete,
  ArcadeRun as SentenceScrambleRun,
  ArcadeRunState as SentenceScrambleRunState,
  SentenceScrambleQuestion,
};

/**
 * Sentence Scramble (FR-4.8 / COR-189): first-class `sentence_scramble` mode so
 * personal bests never share a row with Rapid Fire.
 *
 * Two intentional spellings:
 * - ArcadeMode storage/wire key: `sentence_scramble` (snake_case) — personal-best key
 * - GameType question content id: `sentence-scramble` (hyphenated)
 */
export function createSentenceScrambleRun(timing: ArcadeTiming): Promise<ArcadeRun> {
  return learningApi.createArcadeRun({
    mode: 'sentence_scramble',
    timing,
    types: ['sentence-scramble'],
    limit: 5,
  });
}

export function answerSentenceScramble(
  runId: string,
  questionId: string,
  orderedTokens: readonly string[]
): Promise<ArcadeAnswerResult> {
  return learningApi.answerArcadeQuestion(runId, {
    questionId,
    answer: orderedTokens.join(' '),
  });
}

export function getSentenceScrambleRun(runId: string): Promise<ArcadeRunState> {
  return learningApi.getArcadeRun(runId);
}

export function completeSentenceScrambleRun(runId: string): Promise<ArcadeCompleteResult> {
  return learningApi.completeArcadeRun(runId);
}

export function abandonSentenceScrambleRun(runId: string): Promise<ArcadeAbandonResult> {
  return learningApi.abandonArcadeRun(runId);
}

/**
 * Owner-scoped arcade personal bests (FR-4.7–4.10 / COR-190).
 * No user id param — the JWT principal scopes the list (AC-06 / COR-188).
 */
export function getArcadePersonalBests(): Promise<ArcadePersonalBestsResponse> {
  return learningApi.getArcadePersonalBests();
}

export function createRapidFireRun(timing: ArcadeTiming): Promise<ArcadeRun> {
  return learningApi.createArcadeRun({
    mode: 'rapid_fire',
    timing,
    types: ['word-match', 'fill-in-blank'],
    limit: 10,
  });
}

export function answerRapidFireQuestion(
  runId: string,
  questionId: string,
  answer: string
): Promise<ArcadeAnswerResult> {
  return learningApi.answerArcadeQuestion(runId, { questionId, answer });
}

export function getRapidFireRun(runId: string): Promise<ArcadeRunState> {
  return learningApi.getArcadeRun(runId);
}

export function completeRapidFireRun(runId: string): Promise<ArcadeCompleteResult> {
  return learningApi.completeArcadeRun(runId);
}

export function abandonRapidFireRun(runId: string): Promise<ArcadeAbandonResult> {
  return learningApi.abandonArcadeRun(runId);
}

/**
 * Survival is lives-only: `usesTimedSprintMechanics` excludes it, so the server
 * returns `deadlineAt: null` and no speed bonus under either timing. Posting a
 * fixed `standard` keeps identical play in one `(mode, timing)` personal-best
 * bucket instead of forking it on a choice with no mechanical effect.
 */
export function createSurvivalRun(): Promise<ArcadeRun> {
  return learningApi.createArcadeRun({
    mode: 'survival',
    timing: 'standard',
    types: ['word-match', 'fill-in-blank'],
    limit: 10,
  });
}

export function answerSurvivalQuestion(
  runId: string,
  questionId: string,
  answer: string
): Promise<ArcadeAnswerResult> {
  return learningApi.answerArcadeQuestion(runId, { questionId, answer });
}

export function getSurvivalRun(runId: string): Promise<ArcadeRunState> {
  return learningApi.getArcadeRun(runId);
}

export function completeSurvivalRun(runId: string): Promise<ArcadeCompleteResult> {
  return learningApi.completeArcadeRun(runId);
}

export function abandonSurvivalRun(runId: string): Promise<ArcadeAbandonResult> {
  return learningApi.abandonArcadeRun(runId);
}
